import {
  Form,
  Input,
  Button,
  DatePicker,
  Col,
  Row,
  Space,
  Select,
  InputNumber,
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import moment from 'moment';
import { useEffect, useState } from 'react';
import {
  Project,
  ProjectStatus,
  projectStatusKorean,
} from '../../modules/projects';

const { Option } = Select;

interface ProjectCreateUpdateComponentProps {
  onCreate: (project: Project) => void;
  onCancel: () => void;
  openSelectAddressModal: () => void;
  project?: Project;
  projectLocation?: string;
  projectConfig: any;
}

function ProjectCreateUpdateComponent({
  onCreate,
  onCancel,
  openSelectAddressModal,
  project,
  projectLocation,
  projectConfig,
}: ProjectCreateUpdateComponentProps) {
  const [mount, setMount] = useState(true);
  const [projectTypeList, setProjectTypeList] = useState([]);
  const [form] = Form.useForm();
  const dateFormat = 'YYYY-MM-DD';
  let projectObj;
  let title;
  if (!!project) {
    title = '수정';
    projectObj = {
      ...project,
      startDate:
        project.startDate === ''
          ? moment()
          : moment(project.startDate, 'YYYY-MM-DD'),
      endDate:
        project.endDate === ''
          ? moment()
          : moment(project.endDate, 'YYYY-MM-DD'),
    };
  } else {
    title = '생성';
    projectObj = {
      status: ProjectStatus.IN_PROGRESS,
      startDate: moment(),
      endDate: moment(),
    };
  }

  useEffect(() => form.resetFields(), [project, form]);
  useEffect(() => {
    if (projectLocation) form.setFieldsValue({ location: projectLocation });
  }, [projectLocation, form]);

  useEffect(() => {
    if (!mount) return;
    let children: any = [];
    let projectTypeIndex = 0;

    children = [];
    for (const projectType of projectConfig.types.sort((a: any, b: any) =>
      a.name.localeCompare(b.name),
    )) {
      children.push(
        <Option key={projectTypeIndex++} value={projectType.name}>
          {projectType.name}
        </Option>,
      );
    }
    setProjectTypeList(children);

    return () => setMount(false);
  }, [projectConfig, mount]);

  const validateStartDate = (value: string) => {
    if (form.getFieldValue('endDate')) {
      const startDate = new Date(value);
      const endDate = new Date(form.getFieldValue('endDate'));

      if (startDate >= endDate)
        return Promise.reject(
          new Error('공사시작일은 공사종료일보다 빨라야 합니다.'),
        );
    }
    return Promise.resolve();
  };

  const validateEndDate = (value: string) => {
    if (form.getFieldValue('startDate')) {
      const startDate = new Date(form.getFieldValue('startDate'));
      const endDate = new Date(value);
      if (startDate >= endDate)
        return Promise.reject(
          new Error('공사종료일은 공사시작일보다 늦어야 합니다.'),
        );
    }
    return Promise.resolve();
  };

  return (
    <>
      <Row>
        <Col flex={1}>
          <h2>공사 {title}</h2>
        </Col>
      </Row>
      <Form
        form={form}
        name="control-hooks"
        layout="vertical"
        onFinish={onCreate}
        initialValues={projectObj}
      >
        <Row justify="space-between">
          <Col span={11}>
            <Form.Item
              name="name"
              label="공사명"
              rules={[
                {
                  required: true,
                  message: '공사명을 입력하세요.',
                },
              ]}
            >
              <Input data-test="project-name" />
            </Form.Item>
          </Col>
          <Col span={11}>
            <Form.Item
              name="status"
              label="상태"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select allowClear placeholder="상태를 선택 하세요.">
                <Option value="2">{projectStatusKorean['2']}</Option>
                <Option value="0">{projectStatusKorean['0']}</Option>
                <Option value="1">{projectStatusKorean['1']}</Option>
                <Option value="3">{projectStatusKorean['3']}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <Form.Item
              name="location"
              label="주소"
              rules={[
                {
                  required: true,
                  message: '현장 주소를 입력 하세요.',
                },
              ]}
            >
              <Input onFocus={openSelectAddressModal} />
            </Form.Item>
          </Col>
          <Col span={11}>
            <Form.Item name="detailedLocation" label="상세주소">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <Form.Item
              name="startDate"
              label="공사시작일"
              rules={[
                {
                  required: true,
                  message: '공사시작일을 선택해 주세요.',
                },
                {
                  validator: (_, value) => validateStartDate(value),
                },
              ]}
            >
              <DatePicker format={dateFormat} />
            </Form.Item>
          </Col>
          <Col span={11}>
            <Form.Item
              name="endDate"
              label="공사종료일"
              validateFirst={true}
              rules={[
                {
                  required: true,
                  message: '공사종료일을 선택해 주세요.',
                },
                {
                  validator: (_, value) => validateEndDate(value),
                },
              ]}
            >
              <DatePicker format={dateFormat} />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col span={11}>
            <Form.Item name="type" label="종류">
              <Select style={{ width: '100%' }} allowClear>
                {projectTypeList}
              </Select>
            </Form.Item>
          </Col>
          <Col span={11}>
            <Form.Item name="area" label="면적(m²)">
              <InputNumber
                style={{ width: '100%' }}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={(value) => value!.replace(/₩\s?|(,*)/g, '')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <Form.Item name="owner" label="발주자">
              <Input />
            </Form.Item>
          </Col>
          <Col span={11}>
            <Form.Item name="description" label="설명">
              <TextArea rows={4} />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Form.Item>
            <Space size="middle">
              <Button htmlType="button" onClick={onCancel}>
                취소
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                data-test="submit-project-btn"
              >
                확인
              </Button>
            </Space>
          </Form.Item>
        </Row>
      </Form>
    </>
  );
}

export default ProjectCreateUpdateComponent;
